/**
 * DirectSaleCreateView — /sales/new  견적 없는 판매 등록(백엔드 V장 "견적 없는 판매 등록" A · a · i, 2026-11-02)
 *
 * 거래처 하나(자동완성) · 담당자(등록하는 나 — 고르지 않음) · 메모 · 품목 줄(등록된 제품 찾기 · 컨디션 · 수량 · 단가)을 모아
 * POST /sales-orders 로 보낸다. 뒤에서 견적이 저절로 서고(딜은 안 만든다 — 2026-11-03 딜 없는 판매) 견적 채택과 같은 길로 판매가 선다.
 * 저장 뒤 /sales?open=<id> 로 돌아가 방금 만든 판매를 펼친다. 같은 제품을 두 번 골라도 줄을 합치지 않는다(컨디션이 다를 수 있다).
 * ?from=<판매 id>&items=<품목 행 id,…> 로 열리면 그 판매의 거래처 · 담당자 · 고른 품목(컨디션 · 수량 · 단가)을 채운다 — 취소 뒤 다시 등록.
 * 다시 그리는 곳은 .ds-lines(줄 표 · 합계 · 버튼 줄) 하나 — 자동완성 둘은 그 밖이라 다시 그려도 새로 안 생긴다.
 */
import { SolaView, htmlSolaWithInstance, navigate } from '@hm-spt/sola';
import * as api from '../api/client';
import type { DirectSaleInput, DirectSaleProductMatch, SalesOrderItemView } from '../api/client';
import { AutocompleteInput } from '../components/AutocompleteInput';
import type { FreeTextItem } from '../components/AutocompleteInput';
import { assert } from '../lib/assert';
import { getCurrentEmail } from '../lib/auth';
import { escapeHtml, fmtNumber } from '../lib/cells';
import { refreshKeepingFocus } from '../lib/keep-focus';
import type { DirectSaleFormData, DirectSaleFormLine, DirectSaleMatchRow, ReregisterSource } from './direct-sale/types';
import { conditionOptionsOf, configChipOf, matchRowConditionText, matchRowsOf, renderStockChip } from './direct-sale/line-cells';

const PRODUCT_MATCH_LIMIT = 10;   // 제품 찾기 한 번에 보이는 줄 — 자동완성 기본과 같다
const SUBMIT_CONFIRM_TEXT = '판매를 등록합니다 — 있는 실물은 묶이고 모자란 만큼 구매 요청(니즈)이 납니다.';
const LIST_HREF = '/sales';

export class DirectSaleCreateView extends SolaView<DirectSaleFormData> {
  override events = {
    click:  { '@remove-line': 'handleRemoveLine', '@submit': 'handleSubmit' },
    change: { '@line-condition': 'handleLineCondition' },
    input:  { '@note': 'handleNote', '@line-quantity': 'handleLineQuantity', '@line-price': 'handleLinePrice' },
  };

  /** 거래처 — 고르면 customer. 새 거래처 만들기는 여기서 안 한다(거래처 페이지 몫) */
  private readonly customerAC: AutocompleteInput<api.CustomerRow>;
  /** 제품 찾기 — 제품 × 컨디션 줄. 고르면 줄 하나 더(고른 컨디션 · 수량 1 · 단가 null) */
  private readonly productAC: AutocompleteInput<DirectSaleMatchRow>;

  /** 다시 등록의 출처 — null 이면 빈 폼 */
  private readonly sourceOrNull: ReregisterSource | null;

  constructor(sourceOrNull: ReregisterSource | null) {
    super();
    this.sourceOrNull = sourceOrNull;
    this.data = { customer: null, me: null, note: '', lines: [], saving: false, error: null };
    this.customerAC = new AutocompleteInput<api.CustomerRow>({
      placeholder: '거래처 이름',
      fetchSuggestions: (q, signal) => api.fetchCustomers(q, signal),
      renderItem: (customer) => escapeHtml(customer.name),
      onSelect: (item) => this.handleCustomerSelect(item),
    });
    this.productAC = new AutocompleteInput<DirectSaleMatchRow>({
      placeholder: '제품 찾기 — 파트넘버 · 디스크립션 · 제품번호',
      className: 'autocomplete-input-wide',
      fetchSuggestions: async (q) => matchRowsOf(await api.fetchDirectSaleProductMatches(q, PRODUCT_MATCH_LIMIT)),
      renderItem: (row) => `${configChipOf(row.product)}${escapeHtml(row.product.option_part === null ? '—' : row.product.option_part)} <span class="ac-dim">${escapeHtml(row.product.description === null ? '' : row.product.description)} · ${escapeHtml(row.product.hclt_number)} · ${escapeHtml(matchRowConditionText(row))}</span>`,
      onSelect: (item) => this.handleProductSelect(item),
    });
  }

  override getTitle(): string {
    return '판매 등록 — HCLTech';
  }

  /** 담당자(나) 한 번 · 다시 등록이면 그 판매의 칸들 */
  override onMount(): void {
    void this.loadMe();
    if (this.sourceOrNull !== null) void this.loadReregister(this.sourceOrNull);
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-direct-sale pv-page">
        <header class="page-header">
          <h1>판매 등록</h1>
          <a class="btn-ghost" data-link href="${LIST_HREF}">← 판매 현황</a>
        </header>
        <section class="page-body pv-body">
          <section class="form-card pc-card ds-card">
            <h2>판매 정보</h2>
            <div class="form-grid ds-grid">
              <div class="form-field"><span class="form-label">거래처 <b class="pc-req">*</b></span>${this.customerAC}</div>
              <div class="form-field"><span class="form-label">담당자</span><span class="ds-assignee">${this.renderAssignee()}</span></div>
              <label class="form-field ds-span-2"><span class="form-label">메모</span><textarea data-action="note" rows="2" placeholder="판매 메모(선택)"></textarea></label>
            </div>
          </section>
          <section class="form-card pc-card ds-card">
            <h2>품목</h2>
            <div class="ds-product-search">${this.productAC}</div>
            <div class="ds-lines">${this.renderLines()}</div>
          </section>
          <div class="ds-footer">${this.renderFooter()}</div>
        </section>
      </div>`;
  }

  // ─── 불러오기 ───
  /** 담당자 = 등록하는 나 — 담당자 목록에서 로그인 메일 줄 하나(이름을 보이려고). 못 받으면 메일만 보인다 */
  private async loadMe(): Promise<void> {
    try {
      const assignees = await api.fetchActiveAssignees();
      const me = assignees.find((user) => user.email === getCurrentEmail());
      this.data = { ...this.data, me: me === undefined ? null : me };
      this.refreshRegion('.ds-assignee', () => this.renderAssignee());
    } catch (e) {
      this.data = { ...this.data, error: `담당자(나)를 못 받았습니다: ${e instanceof Error ? e.message : String(e)}` };
      this.refreshLines();
    }
  }

  /** 판매 상세를 받아 거래처 · 담당자 · 고른 품목 줄을 채운다. 가용 수는 제품번호로 찾기를 다시 불러 그 제품 줄을 쓴다. 실패는 error 한 줄 */
  private async loadReregister(source: ReregisterSource): Promise<void> {
    try {
      const detail = await api.fetchSalesOrder(source.sales_order_id);
      const items = detail.items.filter((item) => source.quote_item_ids.includes(item.quote_item_id));
      const matches = await Promise.all(items.map(async (item) => {
        const rows = await api.fetchDirectSaleProductMatches(item.hclt_number, PRODUCT_MATCH_LIMIT);
        return rows.find((row) => row.hclt_product_id === item.hclt_product_id) ?? null;
      }));
      const bMissing = items.length !== source.quote_item_ids.length;
      this.data = {
        ...this.data,
        customer: detail.customer_id === null || detail.customer_name === null ? null : { id: Number(detail.customer_id), name: detail.customer_name },
        lines: items.map((item, index) => reregisterLineOf(item, matches[index] ?? null)),
        error: bMissing ? '다시 등록할 품목 일부를 못 찾았습니다' : null,
      };
      if (this.data.customer !== null) this.customerAC.setValue(this.data.customer.name);
    } catch (e) {
      this.data = { ...this.data, error: `다시 등록할 판매를 못 받았습니다: ${e instanceof Error ? e.message : String(e)}` };
    }
    this.refreshLines();
  }

  /** .ds-lines(표 · 합계)와 .ds-footer(버튼 줄)를 다시 그린다 — 수량 · 단가 칸에 치던 포커스 · 커서는 지킨다(lib/keep-focus). 버튼 줄엔 글 칸이 없다 */
  private refreshLines(): void {
    refreshKeepingFocus(this._rootElement ?? null, '.ds-lines', () => this.refreshRegion('.ds-lines', () => this.renderLines()));
    this.refreshRegion('.ds-footer', () => this.renderFooter());
  }

  // ─── 그리기 ───
  /** 담당자 — 등록하는 나 "오현민 · 메일"(고르지 않는다 — 서버가 등록한 사람으로 채운다). 받기 전 · 못 찾으면 로그인 메일만 */
  private renderAssignee(): string {
    const { me } = this.data;
    const email = getCurrentEmail();
    return escapeHtml(me === null ? (email === null ? '나(등록하는 사람)' : email) : `${me.name} · ${me.email}`);
  }

  /** 품목 표 + 합계 줄 */
  private renderLines(): string {
    const rows = this.data.lines.length === 0
      ? '<tr><td colspan="8" class="ds-empty">위에서 제품을 찾아 넣으세요</td></tr>'
      : this.data.lines.map((line, index) => renderLineRow(line, index)).join('');
    return `
      <table class="pv-table ds-table">
        <thead><tr><th>#</th><th>파트넘버</th><th>디스크립션</th><th>컨디션</th><th class="ds-num">수량</th><th class="ds-num">단가</th><th class="ds-num">금액</th><th></th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td colspan="6" class="ds-num">합계(공급가)</td><td class="ds-num">${fmtNumber(totalKrwOf(this.data.lines))}</td><td></td></tr></tfoot>
      </table>`;
  }

  /** 카드 아래 오른쪽 — 막힌 까닭 · 에러 · [판매 등록] */
  private renderFooter(): string {
    const blockedOrNull = blockedReasonOrNull(this.data);
    const bLocked = blockedOrNull !== null || this.data.saving;
    return `
      ${blockedOrNull === null ? '' : `<span class="ds-blocked">${escapeHtml(blockedOrNull)}</span>`}
      ${this.data.error === null ? '' : `<span class="ds-error">${escapeHtml(this.data.error)}</span>`}
      <button type="button" class="btn-primary" data-action="submit"${bLocked ? ' disabled' : ''}>${this.data.saving ? '등록 중…' : '판매 등록'}</button>`;
  }

  // ─── 고르기 ───
  private handleCustomerSelect(item: api.CustomerRow | FreeTextItem): void {
    assert(!('__new' in item), '거래처 자동완성은 allowFreeText 를 안 켰다 — 새 거래처 줄이 올 수 없다');
    this.data = { ...this.data, customer: { id: item.id, name: item.name }, error: null };
    this.customerAC.setValue(item.name);
    this.refreshLines();
  }

  /** 고른 줄의 컨디션이 들어간다(셀렉트로 바꿀 수 있다). 재고 없는 줄은 '' — 사람이 고른다 */
  private handleProductSelect(item: DirectSaleMatchRow | FreeTextItem): void {
    assert(!('__new' in item), '제품 자동완성은 allowFreeText 를 안 켰다 — 새 제품 줄이 올 수 없다');
    const line: DirectSaleFormLine = { product: item.product, condition: item.condition, quantity: 1, unitPriceKrw: null };
    this.data = { ...this.data, lines: [...this.data.lines, line], error: null };
    this.productAC.setValue('');
    this.refreshLines();
  }

  // ─── 칸 ───
  handleNote(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, note: (el as HTMLTextAreaElement).value };
  }

  // 줄 칸은 data-id 로 줄을 찾는다(없는 줄이면 assert)
  handleLineCondition(_e: Event, el: HTMLElement): void {
    this.updateLine(el, { condition: (el as HTMLSelectElement).value });
  }

  handleLineQuantity(_e: Event, el: HTMLElement): void {
    const text = (el as HTMLInputElement).value.replaceAll(',', '').trim();
    this.updateLine(el, { quantity: text === '' ? Number.NaN : Number(text) });   // 비우면 NaN — 칸은 비어 보이고 막힌 까닭에 뜬다
  }

  handleLinePrice(_e: Event, el: HTMLElement): void {
    const text = (el as HTMLInputElement).value.replaceAll(',', '').trim();
    this.updateLine(el, { unitPriceKrw: text === '' || Number.isNaN(Number(text)) ? null : Number(text) });
  }

  handleRemoveLine(_e: Event, el: HTMLElement): void {
    const index = lineIndexOf(el, this.data.lines);
    this.data = { ...this.data, lines: this.data.lines.filter((_line, at) => at !== index) };
    this.refreshLines();
  }

  /** 막힘 없으면 확인창 → createDirectSale → location '/sales?open=<id>'. 실패는 error 한 줄 */
  async handleSubmit(): Promise<void> {
    if (blockedReasonOrNull(this.data) !== null || this.data.saving) return;
    if (!confirm(SUBMIT_CONFIRM_TEXT)) return;
    this.data = { ...this.data, saving: true, error: null };
    this.refreshLines();
    try {
      const detail = await api.createDirectSale(directSaleInputOf(this.data));
      navigate(`${LIST_HREF}?open=${encodeURIComponent(detail.sales_order_id)}`);
    } catch (e) {
      this.data = { ...this.data, saving: false, error: `판매 등록 실패: ${e instanceof Error ? e.message : String(e)}` };
      this.refreshLines();
    }
  }

  /** 줄 하나의 칸을 바꾸고 .ds-lines 를 다시 그린다 */
  private updateLine(el: HTMLElement, patch: Partial<DirectSaleFormLine>): void {
    const index = lineIndexOf(el, this.data.lines);
    this.data = { ...this.data, lines: this.data.lines.map((line, at) => at === index ? { ...line, ...patch } : line), error: null };
    this.refreshLines();
  }
}

// ─── 그리기 — 줄 하나 ───
function renderLineRow(line: DirectSaleFormLine, index: number): string {
  const amount = line.unitPriceKrw === null || !Number.isFinite(line.quantity) ? '' : fmtNumber(line.unitPriceKrw * line.quantity);
  return `
    <tr>
      <td>${index + 1}</td>
      <td>${escapeHtml(line.product.option_part === null ? '—' : line.product.option_part)}</td>
      <td class="ds-desc">${configChipOf(line.product)}${escapeHtml(line.product.description === null ? '' : line.product.description)}</td>
      <td class="ds-cond ds-ctrl"><select data-action="line-condition" data-id="${index}"><option value="">고르기</option>${conditionOptionsOf(line)}</select>${renderStockChip(line)}</td>
      <td class="ds-num ds-ctrl"><input type="text" inputmode="numeric" class="ds-qty" data-action="line-quantity" data-id="${index}" value="${Number.isNaN(line.quantity) ? '' : line.quantity}"></td>
      <td class="ds-num ds-ctrl"><input type="text" inputmode="numeric" class="ds-price" data-action="line-price" data-id="${index}" value="${line.unitPriceKrw === null ? '' : line.unitPriceKrw}" placeholder="원"></td>
      <td class="ds-num ds-amount">${amount}</td>
      <td class="ds-ctrl"><button type="button" class="btn-icon ds-remove" data-action="remove-line" data-id="${index}" title="빼기" aria-label="빼기">✕</button></td>
    </tr>`;
}

/** 칸의 data-id → 줄 번호. 없는 줄이면 assert */
function lineIndexOf(el: HTMLElement, lines: readonly DirectSaleFormLine[]): number {
  const index = Number(el.dataset.id);
  assert(Number.isInteger(index) && index >= 0 && index < lines.length, `줄 칸의 data-id '${String(el.dataset.id)}' 가 줄 ${lines.length}개 밖이다 — renderLineRow 가 붙인 번호만 온다`);
  return index;
}

// ─── 순수 함수 ───

/** 주소 → 다시 등록의 출처. from · items 가 둘 다 있을 때만, 하나라도 비면 null(빈 폼) */
export function reregisterSourceOrNull(query: URLSearchParams | undefined): ReregisterSource | null {
  if (query === undefined) return null;
  const salesOrderId = query.get('from');
  const quoteItemIds = (query.get('items') ?? '').split(',').filter((id) => id !== '');
  if (salesOrderId === null || salesOrderId === '' || quoteItemIds.length === 0) return null;

  return { sales_order_id: salesOrderId, quote_item_ids: quoteItemIds };
}

/** 품목 행 → 판매 등록 줄. 컨디션 · 수량 · 단가는 그대로. 찾기에 없으면(재고 없는 구성 등) 품목 칸으로 제품을 세우고 가용은 [] */
export function reregisterLineOf(item: SalesOrderItemView, matchOrNull: DirectSaleProductMatch | null): DirectSaleFormLine {
  const product: DirectSaleProductMatch = matchOrNull ?? {
    hclt_product_id: item.hclt_product_id,
    hclt_number: item.hclt_number,
    product_type: item.product_type,
    is_config: item.is_config,
    option_part: item.option_part,
    description: item.description,
    available: [],
  };

  return { product, condition: item.condition ?? '', quantity: item.quantity, unitPriceKrw: item.unit_price_krw };
}

/** 저장이 막힌 까닭 — 거래처 없음 · 줄 없음 · 컨디션 빈 줄 · 수량 1 미만 · 단가 빈 줄. 버튼 잠금 + 버튼 옆 한 줄. 막힘 없으면 null */
export function blockedReasonOrNull(data: DirectSaleFormData): string | null {
  const checks: readonly [boolean, string][] = [
    [data.customer === null, '거래처를 고르세요'],
    [data.lines.length === 0, '품목을 하나 이상 넣으세요'],
    [data.lines.some((line) => line.condition === ''), '컨디션을 고르세요'],
    [data.lines.some((line) => !Number.isInteger(line.quantity) || line.quantity < 1), '수량은 1 이상 정수입니다'],
    [data.lines.some((line) => line.unitPriceKrw === null || line.unitPriceKrw < 0), '단가를 넣으세요'],
  ];
  const hit = checks.find(([bBlocked]) => bBlocked);

  return hit === undefined ? null : hit[1];
}

/** 보낼 몸 — blockedReasonOrNull 이 null 일 때만 부른다(assert). 담당자는 늘 null — 서버가 등록한 사람으로 채운다 */
export function directSaleInputOf(data: DirectSaleFormData): DirectSaleInput {
  assert(blockedReasonOrNull(data) === null, `막힌 폼을 보내려 한다 — ${String(blockedReasonOrNull(data))}. handleSubmit 이 먼저 막는다`);
  assert(data.customer !== null, '거래처가 없다 — blockedReasonOrNull 이 막았어야 한다');

  return {
    customer_id: data.customer.id,
    contact_id: null,
    assignee_user_id: null,
    note: data.note.trim() === '' ? null : data.note.trim(),
    lines: data.lines.map((line) => ({
      hclt_product_id: Number(line.product.hclt_product_id),
      condition: line.condition,
      quantity: line.quantity,
      unit_price_krw: line.unitPriceKrw === null ? 0 : line.unitPriceKrw,   // 빈 단가는 위 assert 가 막았다 — 타입을 좁히는 자리
    })),
  };
}

/** 합계(공급가) — Σ 단가 × 수량(빈 단가 · 비운 수량은 0) */
export function totalKrwOf(lines: readonly DirectSaleFormLine[]): number {
  return lines.reduce((sum, line) => sum + (line.unitPriceKrw === null || !Number.isFinite(line.quantity) ? 0 : line.unitPriceKrw * line.quantity), 0);   // 빈 단가 · 비운 수량은 0
}
